import React, { useEffect, useRef, useState } from "react";
// The light player rather than the full one, for the reason `StepRipple` gives:
// this file is a stroked path and a few discs — no expressions, no effects, no
// images — and the light build is the same renderer with all of that left out.
import lottie, { AnimationItem } from "lottie-web/build/player/lottie_light";
import { Check } from "lucide-react";
import checkAnimation from "../../../assets/svg/free trial/Green Check.json";

/**
 * How the file is actually laid out, which is not how it looks.
 *
 * The composition is 1080 square and the mark is centred in it, but it does not
 * fill it: the disc is drawn 489 units across — 45% of the frame — with the
 * rest left for the ripple that pulses out of it and fades. Rendered straight
 * into a 64px box that comes out as a 29px tick swimming in empty space, which
 * is what it looked like at first.
 *
 * So the box is scaled by what has to be true of the picture, the same way
 * `StepRipple` sizes the dashboard's haloes rather than trusting the frame:
 * blow the composition up until the disc inside it is the size asked for, and
 * centre that on the space reserved for it. The ripple is then free to spill
 * past the reserve, which is exactly what it is meant to do.
 *
 * `DISC` is measured off the file — the ring layer's own bounds — not tuned by
 * eye. There is no centre constant to go with it: the art is centred in its
 * frame, so centring the frame centres the art.
 */
const COMP = 1080;
const DISC = 489;

interface GreenCheckProps {
  /** How big the disc should come out, in pixels — the space this reserves in
   *  the layout, whatever the composition around it needs. */
  size?: number;
}

/**
 * The mark at the top of the confirmation: a check that draws itself in, and
 * keeps pulsing.
 *
 * On a loop, the way the dashboard's checklist marks are — same player, same
 * behaviour, so an animated mark means the same thing in both places. The file
 * carries the pulse itself: two discs that scale out of the tick and fade,
 * which is what the loop is for. It replays whole rather than looping the pulse
 * alone, because the pulse keyframes overlap the draw-on and a segment cut out
 * of the middle restarts mid-ripple with a visible seam.
 *
 * Decorative, and `aria-hidden` for it: what has happened is the heading
 * underneath, in words. Nothing here is the only carrier of anything.
 *
 * Two ways it can end up as a plain tick instead, and both are fine: a reader
 * who has asked their system for less movement never gets the draw-on, and a
 * player that fails to load leaves the mark rather than a hole in the page.
 */
const GreenCheck: React.FC<GreenCheckProps> = ({ size = 64 }) => {
  const box = useRef<HTMLSpanElement>(null);
  const [played, setPlayed] = useState(false);

  // The composition at the size that makes its disc `size` across.
  const frame = Math.round((size * COMP) / DISC);

  useEffect(() => {
    const host = box.current;
    if (!host) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let animation: AnimationItem | undefined;
    try {
      animation = lottie.loadAnimation({
        container: host,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: checkAnimation,
      });
    } catch (error) {
      // A tick is the last thing on this screen worth breaking a confirmation
      // for — the static one below is already standing behind it.
      console.error("green check failed to load:", error);
      return;
    }

    // Only once the player has actually put something in the box does the
    // static mark go, so there is never a frame with neither. Checked rather
    // than assumed: a player that returns without drawing does not throw, and
    // taking the tick away on its word is how this ends up an empty space.
    if (host.childElementCount > 0) setPlayed(true);

    return () => {
      animation?.destroy();
      setPlayed(false);
    };
  }, []);

  return (
    // The reserved space is the disc's, not the composition's: the frame around
    // it is far larger and mostly empty, and letting that decide the layout
    // would put a hole the size of a card above the heading.
    <span
      className="relative block flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <span
        ref={box}
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: frame, height: frame }}
      />

      {!played && (
        <span
          aria-hidden
          className="flex h-full w-full items-center justify-center rounded-full bg-[#E9F9F0] text-[#16A34A]"
        >
          <Check
            strokeWidth={2.5}
            style={{ width: size / 2.4, height: size / 2.4 }}
          />
        </span>
      )}
    </span>
  );
};

export default GreenCheck;
